"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  X,
  ChevronDown,
  Save,
  ArrowDownToLine,
  ChevronUp,
  PoundSterling,
  AlertTriangle,
  Clock,
  Receipt,
  Loader2,
  HandCoins,
  Briefcase,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { formatDateDDMMYYYY, formatCurrency, cn } from "@/lib/utils";
import { useTransactionDetail } from "@/hooks/useTransactionsData";
import { useTransactionsPageDataGraphQL, sortFieldMap, buildGraphQLFilterVariable, buildMetricFilter, mergeFilters } from "@/hooks/useTransactionsGraphQL";
import type { GetTransactionsParams } from "@/lib/api/transactions";
import {
  type Transaction,
  type TransactionFilters,
  type SavedFilterPreset,
} from "@/types/transaction";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { TransactionStatusBadge } from "@/components/transactions/transaction-status-badge";
import { FilterSheet } from "@/components/transactions/filter-sheet";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { SelectionToolbar } from "@/components/transactions/selection-toolbar";
import { ErrorBoundary } from "@/components/error-boundary";
import { SendRemindersDialog } from "@/components/transactions/send-reminders-dialog";
import { SendEmailModal } from "@/components/collections/action-modals";
import { AllocateReceiptDialog } from "@/components/transactions/allocate-receipt-dialog";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { 
  CreateDisputeModal,
  AddNoteModal,
  type DisputeFormData,
  type NoteFormData,
} from "@/components/transactions/transaction-action-modals";
import { calculateTransactionTotals } from "@/lib/export-utils";
import { useExportAll } from "@/hooks/useExportAll";
import { ExportProvider } from "@/providers/ExportProvider";
import type { ColumnInput } from "@/types/export";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { CreatePromiseDrawer, type PromiseFormData } from "@/components/collections/create-promise-drawer";
import { AllocationHistoryModal } from "@/components/transactions/allocation-history-modal";

// Default presets
const defaultPresets: SavedFilterPreset[] = [
  {
    id: "preset-1",
    name: "Overdue Invoices",
    filters: {
      customerIds: [],
      transactionTypes: ["invoice"],
      statuses: ["overdue"],
      searchQuery: "",
    },
    isDefault: true,
  },
  {
    id: "preset-2",
    name: "Recent Receipts",
    filters: {
      customerIds: [],
      transactionTypes: ["receipt"],
      statuses: ["completed"],
      searchQuery: "",
    },
    isDefault: true,
  },
];

const DEFAULT_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0 },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'customerName', label: 'Customer Name', visible: true, order: 3 },
  { id: 'transactionType', label: 'Type', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'amount', label: 'Gross Amount', visible: true, order: 6 },
  { id: 'outstandingAmount', label: 'Outstanding Amount', visible: true, order: 7 },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: true, order: 9 },
  { id: 'createdBy', label: 'Created By', visible: true, order: 10 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 11 },
  { id: 'accountCode', label: 'Account Code', visible: false, order: 12 },
  { id: 'netAmount', label: 'Net Amount', visible: false, order: 13 },
  { id: 'vatAmount', label: 'VAT Amount', visible: false, order: 14 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 15 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 16 },
  { id: 'flags', label: 'Flags', visible: true, order: 17 },
  { id: 'onDispute', label: 'On Dispute', visible: true, order: 18 },
  { id: 'currencyGross', label: 'Currency Gross', visible: false, order: 19 },
  { id: 'currencyNet', label: 'Currency Net', visible: false, order: 20 },
  { id: 'currencyVAT', label: 'Currency VAT', visible: false, order: 21 },
  { id: 'currencyOutstanding', label: 'Currency Outstanding', visible: false, order: 22 },
  { id: 'actions', label: 'Actions', visible: true, order: 23 },
];

type SortColumn = keyof Transaction | null;
type SortDirection = "asc" | "desc";
type MetricFilter = "outstanding" | "overdue" | "at-risk" | "unallocated-receipts" | null;

const CSV_COLUMN_LABELS: Record<string, string> = {
  referenceNumber: "Reference Number",
  transactionDate: "Transaction Date",
  dueDate: "Due Date",
  customerName: "Customer Name",
  transactionType: "Type",
  amount: "Gross Amount",
  currency: "Currency",
  outstandingAmount: "Outstanding Amount",
  status: "Status",
  description: "Description",
  accountCode: "Account Code",
  netAmount: "Net Amount",
  vatAmount: "VAT Amount",
  onDispute: "On Dispute",
  subLedger: "Sub Ledger",
  orderNumber: "Order Number",
  createdBy: "Created By",
  modifiedBy: "Modified By",
};

function formatCsvCellValue(key: string, value: unknown, currency: string): string {
  if (key === "transactionDate" || key === "dueDate") {
    return value ? formatDateDDMMYYYY(value as string) : "";
  }
  if (key === "amount" || key === "outstandingAmount" || key === "netAmount" || key === "vatAmount") {
    return value !== undefined ? formatCurrency(value as number, currency) : "";
  }
  if (key === "onDispute") {
    return value ? "Yes" : "No";
  }
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

function buildCsvContent(data: Transaction[], visibleColumnKeys: string[]): string {
  const header = visibleColumnKeys.map(key => `"${CSV_COLUMN_LABELS[key] || key}"`).join(",");
  const rows = data.map(transaction =>
    visibleColumnKeys.map(key => {
      const value = transaction[key as keyof Transaction];
      const formatted = formatCsvCellValue(key, value, transaction.currency);
      return `"${formatted.replace(/"/g, '""')}"`;
    }).join(",")
  );
  return [header, ...rows].join("\n");
}

function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getEmptyStateMessage(metricFilter: MetricFilter): string {
  switch (metricFilter) {
    case "unallocated-receipts": return "No unallocated receipts found";
    case "outstanding": return "No outstanding invoices found";
    case "overdue": return "No overdue transactions found";
    case "at-risk": return "No at-risk invoices found";
    default: return "No transactions found";
  }
}

function getEmptyStateDescription(metricFilter: MetricFilter, searchQuery: string, hasActiveFilters: boolean): string {
  if (metricFilter === "unallocated-receipts") return "All receipts have been fully allocated";
  if (searchQuery || hasActiveFilters) return "Try adjusting your search or filters";
  return "No transactions match the current criteria";
}

export function TransactionsPage() {
  return (
    <ExportProvider>
      <TransactionsPageInner />
    </ExportProvider>
  );
}

function TransactionsPageInner() {
  const navigate = useNavigate();

  // State persistence key
  const STORAGE_KEY = "transactions-page-state";

  // Load persisted state
  const loadPersistedState = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load persisted state:", error);
    }
    return null;
  };

  const persistedState = loadPersistedState();

  // State
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState(persistedState?.searchQuery || "");
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [selectedMetricFilter, setSelectedMetricFilter] = useState<MetricFilter>(
    persistedState?.selectedMetricFilter || null
  );
  const [filters, setFilters] = useState<TransactionFilters>(
    persistedState?.filters || {
      customerIds: [],
      transactionTypes: [],
      statuses: [],
      currencies: [],
      searchQuery: "",
      outstandingBalanceMin: 0.01, // Default filter: Outstanding Balance > 0
    }
  );
  const [savedPresets, setSavedPresets] = useState<SavedFilterPreset[]>(defaultPresets);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const {
    columns: txColumns,
    savePreferences: saveTxColumnPreferences,
    resetToDefaults: resetTxColumnPreferences,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Transactions, DEFAULT_TRANSACTION_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    txColumns.forEach((col) => { vis[col.id] = col.visible; });
    return vis;
  }, [txColumns]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("transactionDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Build server-side query params from local state
  const serverParams = useMemo((): GetTransactionsParams => {
    const params: GetTransactionsParams = {
      page: currentPage,
      pageSize,
      sortBy: sortColumn ?? undefined,
      sortOrder: sortDirection,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (selectedMetricFilter) params.metricFilter = selectedMetricFilter;
    if (filters.transactionTypes.length > 0) params.transactionType = filters.transactionTypes.join(',');
    if (filters.statuses.length > 0) params.status = filters.statuses.join(',');
    if (filters.customerIds.length > 0) params.customerId = filters.customerIds.join(',');
    if (filters.currencies && filters.currencies.length > 0) params.currency = filters.currencies.join(',');
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.amountMin !== undefined) params.amountMin = filters.amountMin;
    if (filters.amountMax !== undefined) params.amountMax = filters.amountMax;
    if (filters.outstandingBalanceMin !== undefined) params.outstandingMin = filters.outstandingBalanceMin;
    if (filters.outstandingBalanceMax !== undefined) params.outstandingMax = filters.outstandingBalanceMax;
    if (filters.hasCase) params.hasCase = true;
    if (filters.hasPromise) params.hasPromise = true;
    if (filters.onDisputeFilter) params.onDispute = true;
    return params;
  }, [currentPage, pageSize, sortColumn, sortDirection, debouncedSearch, selectedMetricFilter, filters]);

  // Data from GraphQL hook (handles Demo/Live mode) — server-side pagination
  const { transactions, pagination, totals: dashboardData, isLoading, refetch } = useTransactionsPageDataGraphQL(serverParams);

  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // Sheet/Dialog states
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
  const { data: selectedTransaction } = useTransactionDetail(
    detailSheetOpen ? selectedTransactionId : null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [sendCopyInvoiceDialogOpen, setSendCopyInvoiceDialogOpen] = useState(false);
  const [allocateReceiptDialogOpen, setAllocateReceiptDialogOpen] = useState(false);
  const [createCollectionCaseModalOpen, setCreateCollectionCaseModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [createDisputeModalOpen, setCreateDisputeModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [allocationHistoryModalOpen, setAllocationHistoryModalOpen] = useState(false);

  // Persist state changes
  useEffect(() => {
    try {
      const stateToSave = {
        searchQuery,
        selectedMetricFilter,
        filters,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Failed to persist state:", error);
    }
  }, [searchQuery, selectedMetricFilter, filters]);

  // Handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedRows(new Set(transactions.map((t) => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean | "indeterminate") => {
    try {
      const newSelected = new Set(selectedRows);
      if (checked === true) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      setSelectedRows(newSelected);
    } catch (error) {
      console.error("Error in handleSelectRow:", error);
      toast.error("Failed to select transaction");
    }
  };

  const handleMetricCardClick = (metric: MetricFilter) => {
    // Toggle: if clicking the same metric, deselect it
    if (selectedMetricFilter === metric) {
      setSelectedMetricFilter(null);
      setCurrentPage(1);
      toast.success("Filter cleared");
    } else {
      setSelectedMetricFilter(metric);
      setCurrentPage(1);
      setSelectedRows(new Set()); // Clear selection when changing filters
      const metricNames = {
        outstanding: "Total Outstanding",
        overdue: "Overdue",
        "at-risk": "At Risk Amount",
        "unallocated-receipts": "Unallocated Receipts",
      };
      const descriptions = {
        outstanding: "Showing all outstanding/pending invoices",
        overdue: "Showing transactions past their due date",
        "at-risk": "Showing invoices overdue or due within 7 days",
        "unallocated-receipts": "Showing receipts with outstanding balance > 0",
      };
      toast.success(`${metricNames[metric!]}: ${descriptions[metric!]}`);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilters({
      customerIds: [],
      transactionTypes: [],
      statuses: [],
      currencies: [],
      searchQuery: "",
      outstandingBalanceMin: 0.01, // Reset to default filter
    });
    setSelectedMetricFilter(null);
    setActivePresetId(null);
    setCurrentPage(1);
    setSelectedRows(new Set()); // Clear selection when clearing filters
    toast.success("All filters cleared - showing all transactions with outstanding balance > 0");
  };

  const handleApplyPreset = (preset: SavedFilterPreset) => {
    setFilters(preset.filters);
    setSearchQuery(preset.filters.searchQuery || "");
    setActivePresetId(preset.id);
    setCurrentPage(1);
    setFilterSheetOpen(false);
    toast.success(`Filter preset "${preset.name}" applied`);
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      toast.error("Preset name required");
      return;
    }

    const newPreset: SavedFilterPreset = {
      id: `preset-${Date.now()}`,
      name: presetName,
      filters: { ...filters, searchQuery },
    };

    setSavedPresets([...savedPresets, newPreset]);
    setPresetName("");
    setSavePresetDialogOpen(false);
    toast.success(`Filter preset "${newPreset.name}" saved`);
  };

  const handleDeletePreset = (presetId: string) => {
    const preset = savedPresets.find((p) => p.id === presetId);
    if (preset?.isDefault) return;

    setSavedPresets(savedPresets.filter((p) => p.id !== presetId));
    if (activePresetId === presetId) {
      setActivePresetId(null);
    }
    toast.success("Filter preset deleted");
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransactionId(transaction.id);
    setDetailSheetOpen(true);
  };

      // handleDeleteTransaction - available for future use when delete API is implemented
    void ((transaction: Transaction) => {
      setTransactionToDelete(transaction);
      setDeleteDialogOpen(true);
    });

    const confirmDelete = () => {
      if (transactionToDelete) {
        toast.success("Transaction deleted");
        setTransactionToDelete(null);
        setDeleteDialogOpen(false);
      }
    };

    // handleBulkDelete - available for future use when bulk delete API is implemented
    void (() => {
      toast.success(`${selectedRows.size} transactions deleted`);
      setSelectedRows(new Set());
    });

    // Map frontend column IDs to TransactionListSort enum names for the export API.
    // Extends the shared sortFieldMap with columns that are valid for export
    // but not used for sorting on the list query.
    const exportColumnNameMap: Record<string, string> = useMemo(() => ({
      ...sortFieldMap,
      accountCode: "AccountCode",
      netAmount: "NetAmount",
      vatAmount: "VatAmount",
      onDispute: "OnDispute",
      subLedger: "SubLedger",
      orderNumber: "OrderNumber",
      currencyGross: "CurrencyAmount",
      currencyNet: "CurrencyNetAmount",
      currencyVAT: "CurrencyVatAmount",
      currencyOutstanding: "CurrencyOutstandingAmount",
    }), []);

    // Build columns for server-side export based on visible columns
    const getExportColumns = useCallback((): ColumnInput[] => {
      return txColumns
        .filter((col) => col.visible && col.id !== 'actions' && col.id in exportColumnNameMap)
        .map((col) => ({
          name: exportColumnNameMap[col.id],
          displayName: col.label,
        }));
    }, [txColumns, exportColumnNameMap]);

    // Build columns for server-side export with all columns
    const getAllExportColumns = useCallback((): ColumnInput[] => {
      return DEFAULT_TRANSACTION_COLUMNS
        .filter((col) => col.id !== 'actions' && col.id in exportColumnNameMap)
        .map((col) => ({
          name: exportColumnNameMap[col.id],
          displayName: col.label,
        }));
    }, [exportColumnNameMap]);

    // Reuse the same filter builder used by the GraphQL queries
    const getExportFilter = useCallback(
      (): Record<string, unknown> | undefined => {
        const regularFilter = buildGraphQLFilterVariable(serverParams);
        const metricFilterObj = buildMetricFilter(serverParams.metricFilter);
        return mergeFilters(regularFilter, metricFilterObj);
      },
      [serverParams],
    );

    // Server-side export hook for current view columns
    const { triggerExportAll: triggerExportCurrentView } = useExportAll({
      form: 'Transactions',
      getColumns: getExportColumns,
      getFilter: getExportFilter,
      fileName: `transactions-export-${new Date().toISOString().split("T")[0]}`,
      onFinished: () => toast.success('Export completed successfully'),
    });

    // Server-side export hook for all columns
    const { triggerExportAll: triggerExportAllColumns } = useExportAll({
      form: 'Transactions',
      getColumns: getAllExportColumns,
      getFilter: getExportFilter,
      fileName: `transactions-all-columns-export-${new Date().toISOString().split("T")[0]}`,
      onFinished: () => toast.success('Export completed successfully'),
    });

    // Handle export to Excel (server-side)
    const handleExportCurrentView = () => {
      triggerExportCurrentView();
      toast.info('Export started. You will be notified when the file is ready.');
    };

    // Handle export all columns to Excel (server-side)
    const handleExportAllColumns = () => {
      triggerExportAllColumns();
      toast.info('Export started. You will be notified when the file is ready.');
    };

    const handleSendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
  };

  const handleSendCopyInvoice = (email: string, message: string, transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Invoice copies sent to ${email} for ${transactionIds.length} invoice(s)`);
  };

  const handleAllocateReceipt = (receiptId: number, allocations: { invoiceId: number; amount: number }[]) => {
    // In a real app, this would call an API
    toast.success(`Receipt allocated to ${allocations.length} invoice(s)`);
  };

  // Handler for navigating to allocate screen when unallocated receipts are selected
  const handleNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = selectedTransactions.filter(
      (t) => t.transactionType === "receipt" && 
             t.outstandingAmount !== undefined && 
             t.outstandingAmount > 0
    );

    if (unallocatedReceipts.length === 0) {
      toast.error("No unallocated receipts selected");
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setCreateCollectionCaseModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleCreatePromise = (_data?: PromiseFormData) => {
    // Called after promise drawer handles the mutation internally
    setCreatePromiseModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleCreateDispute = (_data?: DisputeFormData) => {
    // Called after dispute modal handles the mutation internally
    setCreateDisputeModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleAddNote = (_data?: NoteFormData) => {
    // Called after note modal handles the mutation internally
    setAddNoteModalOpen(false);
    setSelectedRows(new Set());
  };

  const handleViewAllocationHistory = () => {
    setAllocationHistoryModalOpen(true);
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveTxColumnPreferences(columns);
  };

  // Get selected transactions
  const selectedTransactions = useMemo(() => {
    try {
      return transactions.filter((t) => selectedRows.has(t.id));
    } catch (error) {
      console.error("Error filtering selected transactions:", error);
      return [];
    }
  }, [transactions, selectedRows]);

  // Calculate totals for selected transactions
  const selectionTotals = useMemo(() => {
    try {
      return calculateTransactionTotals(selectedTransactions);
    } catch (error) {
      console.error("Error calculating selection totals:", error);
      return {
        count: 0,
        totalAmount: 0,
        totalOutstanding: 0,
        currency: "GBP",
      };
    }
  }, [selectedTransactions]);


  // Check if there are any non-default filters active
  const hasActiveFilters =
    searchQuery ||
    filters.customerIds.length > 0 ||
    filters.transactionTypes.length > 0 ||
    filters.statuses.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined ||
    // Outstanding balance filter is non-default if it's not 0.01 or if max is set
    (filters.outstandingBalanceMin !== undefined && filters.outstandingBalanceMin !== 0.01) ||
    filters.outstandingBalanceMax !== undefined ||
    filters.hasCase ||
    filters.hasPromise ||
    filters.onDisputeFilter;

  // Count active filters (excluding the default outstanding balance filter)
  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (filters.customerIds.length > 0 ? 1 : 0) +
    (filters.transactionTypes.length > 0 ? 1 : 0) +
    (filters.statuses.length > 0 ? 1 : 0) +
    (filters.currencies && filters.currencies.length > 0 ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.amountMin !== undefined ? 1 : 0) +
    (filters.amountMax !== undefined ? 1 : 0) +
    // Count outstanding balance filter only if it's been modified from default
    ((filters.outstandingBalanceMin !== undefined && filters.outstandingBalanceMin !== 0.01) ||
      filters.outstandingBalanceMax !== undefined ? 1 : 0) +
    (filters.hasCase ? 1 : 0) +
    (filters.hasPromise ? 1 : 0) +
    (filters.onDisputeFilter ? 1 : 0);

  // Check if default filter is active
  const hasDefaultFilter = filters.outstandingBalanceMin === 0.01;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
        </div>
        <p className="text-muted-foreground">Comprehensive view of all transaction records</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card
          className={cn(
            "transition-all border-l-4 border-l-orange-500 cursor-pointer",
            selectedMetricFilter === "outstanding"
              ? "shadow-lg ring-2 ring-orange-500 bg-orange-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("outstanding")}
          title="Click to filter and show all outstanding/pending invoices"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-orange-100">
                    <PoundSterling className="h-4 w-4 text-orange-600" />
                  </div>
                  {selectedMetricFilter === "outstanding" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Total Outstanding
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(dashboardData?.totalOutstanding ?? 0, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  All outstanding transactions
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-red-500 cursor-pointer",
            selectedMetricFilter === "overdue"
              ? "shadow-lg ring-2 ring-red-500 bg-red-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("overdue")}
          title="Click to filter and show transactions past their due date"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-red-100">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  {selectedMetricFilter === "overdue" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Overdue
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(dashboardData?.overdueAmount ?? 0, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Past due date
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-yellow-500 cursor-pointer",
            selectedMetricFilter === "at-risk"
              ? "shadow-lg ring-2 ring-yellow-500 bg-yellow-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("at-risk")}
          title="Click to filter and show invoices overdue or due within 7 days"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-yellow-100">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </div>
                  {selectedMetricFilter === "at-risk" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  At Risk Amount
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(dashboardData?.atRiskAmount ?? 0, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Due within 7 days or overdue
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-purple-500 cursor-pointer",
            selectedMetricFilter === "unallocated-receipts"
              ? "shadow-lg ring-2 ring-purple-500 bg-purple-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("unallocated-receipts")}
          title="Click to filter transactions and show only receipts with outstanding balance > 0"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-purple-100">
                    <PoundSterling className="h-4 w-4 text-purple-600" />
                  </div>
                  {selectedMetricFilter === "unallocated-receipts" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Unallocated Receipts
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(dashboardData?.unallocatedReceiptsAmount ?? 0, "GBP")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {dashboardData?.unallocatedReceiptsCount ?? 0} receipt{(dashboardData?.unallocatedReceiptsCount ?? 0) !== 1 ? 's' : ''} with outstanding balance
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader>
          {/* Header with Title, Search, Filters, and Settings */}
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-xl font-semibold whitespace-nowrap">All Transactions</h2>
            
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by reference, customer, or description..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            
            {/* Filters Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilterSheetOpen(true)}
              className="gap-2 whitespace-nowrap"
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            
            {/* Export and Column Config */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <ArrowDownToLine className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportCurrentView}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ColumnConfigDialog
                columns={txColumns}
                onApply={handleColumnConfigApply}
                onReset={resetTxColumnPreferences}
              />
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Active Metric Filter Indicator */}
            {selectedMetricFilter && (
              <div className={cn(
                "flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border",
                selectedMetricFilter === "unallocated-receipts" && "bg-purple-50 border-purple-200",
                selectedMetricFilter === "outstanding" && "bg-orange-50 border-orange-200",
                selectedMetricFilter === "overdue" && "bg-red-50 border-red-200",
                selectedMetricFilter === "at-risk" && "bg-yellow-50 border-yellow-200"
              )}>
                <Receipt className={cn(
                  "h-3.5 w-3.5",
                  selectedMetricFilter === "unallocated-receipts" && "text-purple-600",
                  selectedMetricFilter === "outstanding" && "text-orange-600",
                  selectedMetricFilter === "overdue" && "text-red-600",
                  selectedMetricFilter === "at-risk" && "text-yellow-600"
                )} />
                <span className={cn(
                  "font-medium",
                  selectedMetricFilter === "unallocated-receipts" && "text-purple-700",
                  selectedMetricFilter === "outstanding" && "text-orange-700",
                  selectedMetricFilter === "overdue" && "text-red-700",
                  selectedMetricFilter === "at-risk" && "text-yellow-700"
                )}>
                  {selectedMetricFilter === "unallocated-receipts" && "Unallocated Receipts"}
                  {selectedMetricFilter === "outstanding" && "Total Outstanding"}
                  {selectedMetricFilter === "overdue" && "Overdue"}
                  {selectedMetricFilter === "at-risk" && "At Risk"}
                  {" "}filter active
                </span>
                <button
                  onClick={() => {
                    setSelectedMetricFilter(null);
                    setCurrentPage(1);
                    toast.success("Metric filter cleared");
                  }}
                  className={cn(
                    "ml-1 hover:opacity-70 transition-opacity",
                    selectedMetricFilter === "unallocated-receipts" && "text-purple-600",
                    selectedMetricFilter === "outstanding" && "text-orange-600",
                    selectedMetricFilter === "overdue" && "text-red-600",
                    selectedMetricFilter === "at-risk" && "text-yellow-600"
                  )}
                  title="Clear metric filter"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Clear All Filters
              </Button>
            )}
            {/* Default Filter Indicator */}
            {hasDefaultFilter && !hasActiveFilters && !selectedMetricFilter && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
                <Filter className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-blue-700">
                  Default filter active: <span className="font-medium">Outstanding Balance &gt; 0</span>
                </span>
                <button
                  onClick={() => {
                    setFilters({
                      ...filters,
                      outstandingBalanceMin: undefined,
                    });
                    toast.info("Default filter removed");
                  }}
                  className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Remove default filter"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        transactions.length > 0 &&
                        transactions.every((t) => selectedRows.has(t.id))
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {columnVisibility.referenceNumber && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("referenceNumber")}
                    >
                      <div className="flex items-center gap-1">
                        Reference Number
                        {sortColumn === "referenceNumber" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.transactionDate && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("transactionDate")}
                    >
                      <div className="flex items-center gap-1">
                        Transaction Date
                        {sortColumn === "transactionDate" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.dueDate && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("dueDate")}
                    >
                      <div className="flex items-center gap-1">
                        Due Date
                        {sortColumn === "dueDate" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.customerName && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("customerName")}
                    >
                      <div className="flex items-center gap-1">
                        Customer Name
                        {sortColumn === "customerName" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.transactionType && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("transactionType")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortColumn === "transactionType" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currency && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("currency")}
                    >
                      <div className="flex items-center gap-1">
                        Currency
                        {sortColumn === "currency" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.amount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Gross
                        {sortColumn === "amount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.outstandingAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("outstandingAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Outstanding
                        {sortColumn === "outstandingAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.status && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortColumn === "status" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.description && <TableHead>Description</TableHead>}
                  {columnVisibility.createdBy && <TableHead>Created By</TableHead>}
                  {columnVisibility.modifiedBy && <TableHead>Modified By</TableHead>}
                  {/* New optional columns */}
                  {columnVisibility.accountCode && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("accountCode")}
                    >
                      <div className="flex items-center gap-1">
                        Account
                        {sortColumn === "accountCode" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.netAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("netAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Net
                        {sortColumn === "netAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.vatAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("vatAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Tax
                        {sortColumn === "vatAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.subLedger && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("subLedger")}
                    >
                      <div className="flex items-center gap-1">
                        Sub Ledger
                        {sortColumn === "subLedger" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.orderNumber && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("orderNumber")}
                    >
                      <div className="flex items-center gap-1">
                        Order Number
                        {sortColumn === "orderNumber" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.flags && <TableHead>Flags</TableHead>}
                  {columnVisibility.onDispute && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("onDispute")}
                    >
                      <div className="flex items-center gap-1">
                        On Dispute
                        {sortColumn === "onDispute" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currencyGross && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("currencyGross")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Currency Gross
                        {sortColumn === "currencyGross" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currencyNet && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("currencyNet")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Currency Net
                        {sortColumn === "currencyNet" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currencyVAT && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("currencyVAT")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Currency VAT
                        {sortColumn === "currencyVAT" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currencyOutstanding && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("currencyOutstanding")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Currency Outstanding
                        {sortColumn === "currencyOutstanding" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.actions && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={Object.values(columnVisibility).filter(Boolean).length + 1}
                      className="h-64 text-center"
                    >
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading transactions...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Object.values(columnVisibility).filter(Boolean).length + 1}
                      className="h-32 text-center"
                    >
                      <div className="flex flex-col items-center justify-center gap-2 py-8">
                        <Receipt className="h-12 w-12 text-muted-foreground/50" />
                        <p className="text-base font-medium text-muted-foreground">
                          {getEmptyStateMessage(selectedMetricFilter)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {getEmptyStateDescription(selectedMetricFilter, searchQuery, !!hasActiveFilters)}
                        </p>
                        {(searchQuery || hasActiveFilters || selectedMetricFilter) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearFilters}
                            className="mt-2"
                          >
                            Clear All Filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.has(transaction.id)}
                          onCheckedChange={(checked) =>
                            handleSelectRow(transaction.id, checked)
                          }
                        />
                      </TableCell>
                      {columnVisibility.referenceNumber && (
                        <TableCell>
                          <button
                            onClick={() => handleViewTransaction(transaction)}
                            className="text-primary hover:underline font-medium"
                          >
                            {transaction.referenceNumber}
                          </button>
                        </TableCell>
                      )}
                      {columnVisibility.transactionDate && (
                        <TableCell>{formatDateDDMMYYYY(transaction.transactionDate)}</TableCell>
                      )}
                      {columnVisibility.dueDate && (
                        <TableCell>
                          {transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.customerName && (
                        <TableCell>
                          <button
                            onClick={() => navigate(`/customers/${transaction.customerId}`)}
                            className="text-primary hover:underline font-medium transition-colors"
                            title={`View details for ${transaction.customerName}`}
                          >
                            {transaction.customerName}
                          </button>
                        </TableCell>
                      )}
                      {columnVisibility.transactionType && (
                        <TableCell>
                          <TransactionTypeBadge type={transaction.transactionType} />
                        </TableCell>
                      )}
                      {columnVisibility.currency && (
                        <TableCell>
                          {transaction.currency}
                        </TableCell>
                      )}
                      {columnVisibility.amount && (
                        <TableCell className="text-right font-bold">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </TableCell>
                      )}
                      {columnVisibility.outstandingAmount && (
                        <TableCell className="text-right font-medium">
                          {transaction.outstandingAmount != null
                            ? formatCurrency(transaction.outstandingAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.status && (
                        <TableCell>
                          {transaction.paymentLinkStatus ? (
                            <Badge variant="outline" className="capitalize">
                              {transaction.paymentLinkStatus.replace(/_/g, " ")}
                            </Badge>
                          ) : (
                            <TransactionStatusBadge status={transaction.status} />
                          )}
                        </TableCell>
                      )}
                      {columnVisibility.description && (
                        <TableCell className="max-w-[300px] truncate">
                          {transaction.description}
                        </TableCell>
                      )}
                      {columnVisibility.createdBy && (
                        <TableCell>{transaction.createdBy || "-"}</TableCell>
                      )}
                      {columnVisibility.modifiedBy && (
                        <TableCell>{transaction.modifiedBy || "-"}</TableCell>
                      )}
                      {/* New optional columns */}
                      {columnVisibility.accountCode && (
                        <TableCell>{transaction.accountCode || "-"}</TableCell>
                      )}
                      {columnVisibility.netAmount && (
                        <TableCell className="text-right">
                          {transaction.netAmount != null
                            ? formatCurrency(transaction.netAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.vatAmount && (
                        <TableCell className="text-right">
                          {transaction.vatAmount != null
                            ? formatCurrency(transaction.vatAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.subLedger && (
                        <TableCell>{transaction.subLedger || "-"}</TableCell>
                      )}
                      {columnVisibility.orderNumber && (
                        <TableCell>{transaction.orderNumber || "-"}</TableCell>
                      )}
                      {columnVisibility.flags && (
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex items-center gap-2">
                              {transaction.onDispute && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                                      <AlertTriangle className="h-4 w-4 text-red-600" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                                    <p className="font-semibold">Disputed</p>
                                    <p className="text-xs">This transaction is under dispute</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {transaction.hasPromise && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                                      <HandCoins className="h-4 w-4 text-blue-600" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                                    <p className="font-semibold">Promise to Pay</p>
                                    <p className="text-xs">Customer has committed to payment</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {transaction.hasCase && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                                      <Briefcase className="h-4 w-4 text-orange-600" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                                    <p className="font-semibold">Collection Case</p>
                                    <p className="text-xs">Part of an active collection case</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {!transaction.onDispute && !transaction.hasPromise && !transaction.hasCase && (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      )}
                      {columnVisibility.onDispute && (
                        <TableCell>
                          {transaction.onDispute ? (
                            <Badge variant="destructive" className="text-xs">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground">No</span>
                          )}
                        </TableCell>
                      )}
                      {columnVisibility.currencyGross && (
                        <TableCell className="text-right">
                          {transaction.currencyGross != null
                            ? formatCurrency(transaction.currencyGross, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.currencyNet && (
                        <TableCell className="text-right">
                          {transaction.currencyNet != null
                            ? formatCurrency(transaction.currencyNet, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.currencyVAT && (
                        <TableCell className="text-right">
                          {transaction.currencyVAT != null
                            ? formatCurrency(transaction.currencyVAT, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.currencyOutstanding && (
                        <TableCell className="text-right">
                          {transaction.currencyOutstanding != null
                            ? formatCurrency(transaction.currencyOutstanding, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.actions && (
                        <TableCell>
                          {/* Actions menu removed as per requirements */}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {transactions.length > 0 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalItems)} of{" "}
                {totalItems} results
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 gap-1">
                        {pageSize}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {[10, 25, 50, 100].map((size) => (
                        <DropdownMenuItem
                          key={size}
                          onClick={() => {
                            setPageSize(size);
                            setCurrentPage(1);
                          }}
                        >
                          {size}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filter Sheet */}
      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onApplyFilters={setFilters}
        savedPresets={savedPresets}
        activePresetId={activePresetId}
        onApplyPreset={handleApplyPreset}
        onDeletePreset={handleDeletePreset}
        onSavePreset={() => setSavePresetDialogOpen(true)}
        onClearAll={handleClearFilters}
      />

      {/* Detail Sheet */}
      <TransactionDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        transaction={selectedTransaction ?? null}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete transaction {transactionToDelete?.referenceNumber}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filters as a preset for quick access later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., High Value Invoices"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>
              <Save className="mr-2 h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminders Dialog */}
      <SendRemindersDialog
        open={sendRemindersDialogOpen}
        onOpenChange={setSendRemindersDialogOpen}
        transactions={selectedTransactions}
        onSend={handleSendReminders}
      />

      {/* Send Copy Invoice Dialog */}
      {selectedTransactions.length > 0 && (
        <SendEmailModal
          open={sendCopyInvoiceDialogOpen}
          onOpenChange={setSendCopyInvoiceDialogOpen}
          customerName={selectedTransactions[0].customerName}
          customerEmail="" // Will be populated from customer contacts
          customerId={selectedTransactions[0].customerId.toString()}
          isBulkEmail={selectedTransactions.length > 1}
          bulkCustomerCount={selectedTransactions.length}
          bulkCustomerIds={selectedTransactions.length > 1 ? [...new Set(selectedTransactions.map(t => t.customerId.toString()))] : []}
        />
      )}

      {/* Allocate Receipt Dialog */}
      <AllocateReceiptDialog
        open={allocateReceiptDialogOpen}
        onOpenChange={setAllocateReceiptDialogOpen}
        receipts={selectedTransactions}
        availableInvoices={transactions}
        onAllocate={handleAllocateReceipt}
      />

      {/* Create Collection Case Drawer */}
      <CreateCaseDrawer
        open={createCollectionCaseModalOpen}
        onOpenChange={setCreateCollectionCaseModalOpen}
        onCaseCreated={handleCreateCollectionCase}
        selectedTransactions={selectedTransactions}
      />

      {/* Create Promise to Pay Drawer */}
      <CreatePromiseDrawer
        open={createPromiseModalOpen}
        onOpenChange={setCreatePromiseModalOpen}
        prePopulatedCustomer={
          selectedTransactions.length > 0 && selectedTransactions[0]
            ? {
                customerId: selectedTransactions[0].customerId.toString(),
                customerName: selectedTransactions[0].customerName || '',
                accountNumber: `ACC-${selectedTransactions[0].customerId.toString().padStart(3, '0')}`,
              }
            : undefined
        }
        prePopulatedTransactions={
          selectedTransactions.length > 0
            ? selectedTransactions.map(t => ({
                id: t.id.toString(),
                headerReference: t.headerReference || t.referenceNumber,
                invoiceDate: new Date(t.transactionDate),
                originalAmount: t.amount,
                balanceRemaining: t.outstandingAmount ?? t.amount,
                daysOverdue: t.dueDate 
                  ? Math.max(0, Math.ceil((new Date().getTime() - new Date(t.dueDate).getTime()) / (1000 * 60 * 60 * 24)))
                  : 0,
              }))
            : undefined
        }
        onSave={handleCreatePromise}
      />

      {/* Create Dispute Modal */}
      <CreateDisputeModal
        open={createDisputeModalOpen}
        onOpenChange={setCreateDisputeModalOpen}
        selectedTransactions={selectedTransactions}
        onSave={handleCreateDispute}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={addNoteModalOpen}
        onOpenChange={setAddNoteModalOpen}
        selectedTransactions={selectedTransactions}
        onSave={handleAddNote}
      />

      {/* Allocation History Modal */}
      <AllocationHistoryModal
        open={allocationHistoryModalOpen}
        onOpenChange={setAllocationHistoryModalOpen}
        transactionId={selectedTransactions.length > 0 ? selectedTransactions[0].id : null}
      />

      {/* Selection Toolbar - Floating at bottom when transactions are selected */}
      {selectedRows.size > 0 && selectedTransactions.length > 0 && selectionTotals && (
        <ErrorBoundary>
          <SelectionToolbar
            selectedCount={selectedRows.size}
            totals={selectionTotals}
            selectedTransactions={selectedTransactions}
            onClearSelection={() => setSelectedRows(new Set())}
            onExportCurrentView={handleExportCurrentView}
            onExportAllColumns={handleExportAllColumns}
            onSendReminders={() => setSendRemindersDialogOpen(true)}
            onAllocateReceipt={handleNavigateToAllocate}
            onSendCopyInvoice={() => setSendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setCreatePromiseModalOpen(true)}
            onCreateDispute={() => setCreateDisputeModalOpen(true)}
            onAddNote={() => setAddNoteModalOpen(true)}
            onViewAllocationHistory={handleViewAllocationHistory}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}
